import React, { useCallback, useEffect, useState } from "react";
import { Customer } from "../../../../Contracts/Customer";
import TabsSelector, { TabKeyValue } from "../../../../Components/Atoms/TabsSelector";
import { useInvoiceStore } from "../../../../Store/Invoice";
import { useAuth0 } from "@auth0/auth0-react";
import LoadingPage from "../../../LoadingPage";
import Table, { Column, DataSourceItem } from "../../../../Components/Atoms/Table";
import { Invoice } from "../../../../Contracts/Invoice";
import { toLocalDateString } from "../../../../Utils/DateUtils";
import { useTeamStore } from "../../../../Store/Team";
import Modal from "../../../../Components/Atoms/Modal";
import { useOnboardingStore } from "../../../../Store/Onboarding";

export interface OpenInvoicesModalProps {
  open: boolean;
  setInvoiceDetailModalDetails: (details: { open: boolean; selectedCustomer?: Customer }) => void;
  selectedCustomer?: Customer;
}

const tabs: TabKeyValue[] = [
  { key: "Open", displayValue: "Upcoming" },
  { key: "Overdue", displayValue: "Overdue" }
];

const OpenInvoicesModal: React.FC<OpenInvoicesModalProps> = ({
  open,
  setInvoiceDetailModalDetails,
  selectedCustomer
}) => {
  const { invoices, getInvoices, isLoading, invoiceBaseUrl } = useInvoiceStore();
  const [currentActiveTab, setCurrentActiveTab] = useState<string>("Open");
  const { formatCurrency } = useTeamStore();
  const { getAccessTokenSilently } = useAuth0();
  const { getAdyenKYCDetails } = useOnboardingStore();

  const columns: Column<Invoice>[] = [
    {
      title: "Invoice Number",
      dataIndex: "invoice",
      key: "invoice",
      width: "w-44",
      render: d => (
        <a
          href={`${invoiceBaseUrl}${d.data.collectible_invoice_id}`}
          className="text-blue-600"
          target="_blank"
          rel="noreferrer noopener"
        >
          {d.invoice}
        </a>
      )
    },
    {
      title: "Total",
      dataIndex: "total",
      key: "total",
      width: "w-44"
    },
    {
      title: "Date Issued",
      dataIndex: "dateIssued",
      key: "dateIssued",
      width: "w-44",
      render: d => (
        <span title={toLocalDateString(d.data.issue_date, true)}>
          {toLocalDateString(d.data.issue_date)}
        </span>
      )
    },
    {
      title: "Due Date",
      dataIndex: "dueDate",
      key: "dueDate",
      width: "w-44",
      render: d => (
        <span title={toLocalDateString(d.data.due_date, true)}>
          {toLocalDateString(d.data.due_date)}
        </span>
      )
    }
  ];

  const refreshData = useCallback(async () => {
    selectedCustomer &&
      (await Promise.all([
        getInvoices(
          getAccessTokenSilently,
          undefined,
          undefined,
          undefined,
          `status:eq:${currentActiveTab};collectible_invoice_customer_id:eq:${selectedCustomer.id}`
        ),
        getAdyenKYCDetails(getAccessTokenSilently)
      ]));
  }, [getInvoices, getAccessTokenSilently, selectedCustomer, currentActiveTab, getAdyenKYCDetails]);

  useEffect(() => {
    refreshData();
  }, [open, currentActiveTab, refreshData]);

  const dataSource: DataSourceItem<Invoice>[] = invoices.map(invoice => ({
    key: invoice.id.toString(),
    invoice: invoice.invoice_number,
    dateIssued: toLocalDateString(invoice.issue_date),
    dueDate: toLocalDateString(invoice.due_date),
    total: formatCurrency(invoice.total),
    data: invoice
  }));

  return (
    <Modal
      isOpen={open}
      onClose={() =>
        setInvoiceDetailModalDetails({
          open: false,
          selectedCustomer: undefined
        })
      }
      title="Open Invoices"
      size="small"
      closable
      footer={null}
    >
      <TabsSelector
        tabs={tabs}
        currentActiveTab={currentActiveTab}
        setCurrentActiveTab={setCurrentActiveTab}
        className="mx-2"
      />
      {isLoading ? (
        <div className="h-80 flex w-full items-center justify-center">
          <LoadingPage />
        </div>
      ) : dataSource.length === 0 ? (
        <div className="h-80 flex w-full items-center justify-center text-gray-500">
          No data found
        </div>
      ) : (
        <div className="px-2 h-80 overflow-y-auto">
          <Table columns={columns} dataSource={dataSource} />
        </div>
      )}
    </Modal>
  );
};

export default OpenInvoicesModal;
